import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, Plus, Pencil, Trash2, X, AlertTriangle, FolderPlus, Settings2, ChevronDown, ChevronRight, FileSpreadsheet, Plane, Laptop } from "lucide-react";
import {
  getCategoriasOrcamento, postCategoriaOrcamento, putCategoriaOrcamento, deleteCategoriaOrcamento,
  getStatusCategorias, putStatusCategoria,
  getDespesasGerais, postDespesaGeral, putDespesaGeral, deleteDespesaGeral,
  getTicketsViagemDespesasGerais,
  postGrupoViagemDespesaGeral, putGrupoViagemDespesaGeral, deleteGrupoViagemDespesaGeral,
  getEquipamentosTicketMedio, getReconciliacaoKitsAdmissao,
  postGrupoKitAdmissaoDespesaGeral, putGrupoKitAdmissaoDespesaGeral, deleteGrupoKitAdmissaoDespesaGeral,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import ComboBoxFiltravel from "../../componets/ComboBoxFiltravel";
import { KpiRowOrcamento, Sparkline, GaugeProgresso, TrendAccessory } from "../dashboard/KpiCard";
import {
  MESES_ABREV, formatCurrency,
  calcValorUnitario, calcValorMensalBase, calcProjecaoMensal, calcTotalAnual, somarProjecoes,
} from "../../utils/orcamento";
// Mesmo código/constantes de categoria de Despesas de Viagens usados em Custos Gerais por
// Projeto — Despesas Gerais usa o mesmo mecanismo de "Lançar Viagem" (ticket médio travado +
// lançamento em grupo), mas com um ticket ÚNICO/global ("Backoffice", ver ModalGrupoViagemDespesa
// abaixo) compartilhado por todos os departamentos, em vez de um ticket por departamento. Mesma
// origem para "Lançar Kit Admissão" (2.7), que usa o ticket já cadastrado em Premissas ·
// Equipamentos e Materiais em vez de um ticket próprio.
import {
  CODIGO_CATEGORIA_VIAGENS_PREFIXO,
  CODIGOS_SUBCATEGORIAS_VIAGEM_DIAS, CODIGO_CATEGORIA_PASSAGEM_AEREA, CODIGO_CATEGORIA_VERBA_RELACIONAMENTO,
  CODIGO_CATEGORIA_EQUIPAMENTOS_PREFIXO, KIT_ADMISSAO_LABELS, ITENS_KIT_ADMISSAO_COMUNS, ITENS_KIT_ADMISSAO_VARIAVEIS,
} from "../../constants/custosGeraisProjeto";
// Novo modelo simples de item ("faixas por período", padrão Custos Gerais por Projeto) — só
// 2.6.11 Assinaturas e Sistemas libera "quantidade_usuarios" como tipo de lançamento exclusivo.
import {
  TIPOS_LANCAMENTO_DESPESA_OPTIONS, tiposPermitidosParaCategoriaDespesa, categoriaEhEspecialDespesa,
} from "../../constants/despesasGerais";

// Paleta padrão da plataforma (mesma usada em novasVendas.js / margemBruta.js / dadosLuciano.js)
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
  categoriaBg: isLight ? "#EDE0FB" : "#251A45",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
  success: "#10B981",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);
const OPCOES_MES = MESES_ABREV.map((label, i) => ({ value: i + 1, label }));

// Larguras fixas das colunas Código/Item — travadas na rolagem horizontal, por isso
// precisam de um valor previsível (a coluna Item soma essa largura ao offset da coluna Código).
const COL_CODIGO_WIDTH = 92;
const COL_ITEM_WIDTH = 220;
const COL_ACOES_WIDTH = 76;

const FORM_ITEM_VAZIO = {
  categoria_id: "", nome: "",
  valor_atual: "", mes_inicio_valor: "", mes_fim_valor: "",
  qtde_usuario_atual: "1",
  reajustes: [], expansoes: [],
};
const FORM_CATEGORIA_VAZIO = { codigo: "", nome: "", categoria_pai_id: "", ordem: "0" };

// Só entram no payload/preview as linhas com os 3 campos preenchidos — uma linha em
// preenchimento (ex: só o mês escolhido) fica de fora até ficar completa.
function reajustesDoForm(form) {
  return (form.reajustes || [])
    .filter((r) => r.tipo_indice && r.mes && r.percentual !== "" && !Number.isNaN(parseFloat(r.percentual)))
    .map((r) => ({ tipo_indice: r.tipo_indice, mes: parseInt(r.mes, 10), percentual: parseFloat(r.percentual) }));
}

function resumoReajustes(reajustes) {
  return (reajustes || [])
    .map((r) => `${MESES_ABREV[r.mes - 1]} ${r.tipo_indice === "inflacao" ? "Inflação" : "Reajuste"} +${Number(r.percentual).toFixed(2)}%`)
    .join(" · ");
}

// Só entram no payload/preview as linhas com os 3 campos preenchidos — uma linha em
// preenchimento fica de fora até ficar completa.
function expansoesDoForm(form) {
  return (form.expansoes || [])
    .filter((e) => e.qtde_usuario_novo !== "" && e.mes_inicio && e.mes_fim && !Number.isNaN(parseInt(e.qtde_usuario_novo, 10)))
    .map((e) => ({
      qtde_usuario_novo: parseInt(e.qtde_usuario_novo, 10),
      mes_inicio: parseInt(e.mes_inicio, 10),
      mes_fim: parseInt(e.mes_fim, 10),
      valor_usuario_novo: e.valor_usuario_novo !== "" && e.valor_usuario_novo !== undefined && !Number.isNaN(parseFloat(e.valor_usuario_novo))
        ? parseFloat(e.valor_usuario_novo)
        : null,
    }));
}

function resumoExpansoes(expansoes) {
  return (expansoes || [])
    .map((e) => `${MESES_ABREV[e.mes_inicio - 1]}–${MESES_ABREV[e.mes_fim - 1]}: ${e.qtde_usuario_novo}${
      e.valor_usuario_novo !== null && e.valor_usuario_novo !== undefined ? ` (${formatCurrency(e.valor_usuario_novo)}/usuário)` : ""
    }`)
    .join(" · ");
}

function faixaPadraoVazia() {
  return { mes_inicio: 1, mes_fim: 12, valor: 0, valor_unitario: 0, usuarios: 0 };
}

function construirItemPreview(form) {
  return {
    valor_atual: parseFloat(form.valor_atual) || 0,
    mes_inicio_valor: form.mes_inicio_valor ? parseInt(form.mes_inicio_valor, 10) : null,
    mes_fim_valor: form.mes_fim_valor ? parseInt(form.mes_fim_valor, 10) : null,
    qtde_usuario_atual: parseInt(form.qtde_usuario_atual, 10) || 0,
    reajustes: reajustesDoForm(form),
    expansoes: expansoesDoForm(form),
  };
}

// tipo_lancamento='viagem'/'kit_admissao' ou modelo_valor='faixas': o backend já manda
// valores_mensais prontos (ticket médio × faixas_viagem/faixas_padrao, ou soma de faixas_padrao —
// ver services/despesas_gerais_viagem_svc.py, services/kit_admissao_svc.py e
// services/gestao_orcamentaria_svc.py) porque calcProjecaoMensal só sabe ler itens 'legado'
// (valor_atual/qtde_usuario_atual/reajustes/expansoes). Extraído do componente (função pura, só
// olha os campos do item) para poder ser reaplicado tanto aos itens do ano selecionado quanto
// aos do ano anterior (comparativo do card "Orçado {ano-1}").
function calcularItensComCalculo(itens) {
  return (itens || []).map((item) => {
    if (item.tipo_lancamento === "viagem" || item.tipo_lancamento === "kit_admissao" || item.modelo_valor === "faixas") {
      const projecao = Array.from({ length: 12 }, (_, i) => Number(item.valores_mensais?.[i + 1] || 0));
      return { ...item, valorUnitario: 0, valorBase: 0, projecao, total: calcTotalAnual(projecao) };
    }
    const valorUnitario = calcValorUnitario(item);
    const valorBase = calcValorMensalBase(item);
    const projecao = calcProjecaoMensal(item);
    const total = calcTotalAnual(projecao);
    return { ...item, valorUnitario, valorBase, projecao, total };
  });
}

// tituloModulo: nome exibido ("Despesas Gerais" ou "Custos Gerais")
// departamentos: lista de departamentos disponíveis para este módulo (1 = fixo, sem seletor;
// já filtrada pela role do usuário logado antes de chegar aqui)
export default function GestaoOrcamentariaView({ tituloModulo, departamentos }) {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [departamento, setDepartamento] = useState(departamentos?.[0]);
  const [categorias, setCategorias] = useState([]);
  const [itens, setItens] = useState([]);
  const [itensAnoAnterior, setItensAnoAnterior] = useState([]);
  const [statusCategorias, setStatusCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [selectedItemId, setSelectedItemId] = useState(null);
  const [categoriasColapsadas, setCategoriasColapsadas] = useState(() => new Set());

  // Escolha genérica de categoria mãe — aberta pelo botão-KPI "Lançar Despesa" (não sabe de
  // antemão qual categoria; deixa o usuário escolher antes de cair no fluxo normal de sempre,
  // ModalEscolherSubcategoria, que já sabe lidar com a categoria escolhida).
  const [escolhaGenericaAberta, setEscolhaGenericaAberta] = useState(false);

  function toggleCategoria(categoriaId) {
    setCategoriasColapsadas((prev) => {
      const next = new Set(prev);
      if (next.has(categoriaId)) next.delete(categoriaId);
      else next.add(categoriaId);
      return next;
    });
  }

  const [modalItemAberto, setModalItemAberto] = useState(false);
  const [editandoItem, setEditandoItem] = useState(null);
  const [formItem, setFormItem] = useState(FORM_ITEM_VAZIO);
  const [erroFormItem, setErroFormItem] = useState("");
  const [salvandoItem, setSalvandoItem] = useState(false);

  // Novo modal simples de item (modelo_valor='faixas', padrão Custos Gerais por Projeto) — usado
  // para TODO item novo criado a partir daqui em diante. { categoriaId } = criação nova;
  // { itemExistente } = edição de um item que já é modelo_valor='faixas'. Itens antigos
  // (modelo_valor='legado') continuam editados pelo modal rico acima (modalItemAberto).
  const [modalItemSimples, setModalItemSimples] = useState(null);

  // Botão-KPI de categoria mãe clicado — abre o seletor de subcategoria antes do modal de item.
  const [escolhaCategoriaPai, setEscolhaCategoriaPai] = useState(null);

  // "Lançar Viagem" — mesmo mecanismo de Custos Gerais por Projeto (ticket médio travado +
  // lançamento em grupo), mas com ticket ÚNICO/global (Backoffice), ver ModalGrupoViagemDespesa.
  const [modalGrupoViagem, setModalGrupoViagem] = useState(null); // { grupoExistente? } | {}

  // "Lançar Kit Admissão" — mesmo mecanismo, mas usando o ticket já cadastrado em Premissas ·
  // Equipamentos e Materiais (sem ticket próprio), ver ModalGrupoKitAdmissaoDespesa.
  const [modalGrupoKitAdmissao, setModalGrupoKitAdmissao] = useState(null); // { grupoExistente? } | {}

  // Gerenciar Categorias (lista + ações) — mesmo padrão do "Gerenciar Projetos" em Custos
  // Gerais por Projeto: categoria deixa de ter criar/editar/excluir direto na tabela.
  const [gerenciarCategoriasAberto, setGerenciarCategoriasAberto] = useState(false);
  const [modalCategoriaAberto, setModalCategoriaAberto] = useState(false);
  const [editandoCategoria, setEditandoCategoria] = useState(null);
  const [formCategoria, setFormCategoria] = useState(FORM_CATEGORIA_VAZIO);
  const [erroFormCategoria, setErroFormCategoria] = useState("");
  const [salvandoCategoria, setSalvandoCategoria] = useState(false);

  async function carregarTudo(anoParam, departamentoParam) {
    setLoading(true);
    setErro("");
    try {
      const [cats, its, itsAnoAnterior, status] = await Promise.all([
        getCategoriasOrcamento(departamentoParam),
        getDespesasGerais(anoParam, departamentoParam),
        getDespesasGerais(anoParam - 1, departamentoParam),
        getStatusCategorias(anoParam),
      ]);
      setCategorias(cats || []);
      setItens(its || []);
      setItensAnoAnterior(itsAnoAnterior || []);
      setStatusCategorias(status || []);
      // Categorias pai e subcategorias começam todas recolhidas ao carregar a página.
      setCategoriasColapsadas(new Set((cats || []).map((c) => c.id)));
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Marca/desmarca "já terminei de preencher esta categoria" (categoria mãe) — flag manual,
  // por (categoria, ano); atualização otimista na lista local, sem recarregar tudo de novo.
  async function alternarStatusCategoria(categoriaId, concluido) {
    try {
      await putStatusCategoria(categoriaId, ano, concluido);
      setStatusCategorias((lista) => {
        const existe = lista.some((s) => s.categoria_id === categoriaId);
        if (existe) return lista.map((s) => (s.categoria_id === categoriaId ? { ...s, concluido } : s));
        return [...lista, { categoria_id: categoriaId, ano, projeto_id: null, concluido }];
      });
    } catch (e) {
      setErro(e.message);
    }
  }

  useEffect(() => {
    if (!departamento) return;
    setSelectedItemId(null);
    setCategoriasColapsadas(new Set());
    carregarTudo(ano, departamento);
  }, [ano, departamento]);

  // ── Itens com campos calculados ──────────────────────────────────────────
  // tipo_lancamento='viagem': o backend já manda valores_mensais prontos (ticket médio ×
  // faixas — ver services/despesas_gerais_viagem_svc.py) porque calcProjecaoMensal não sabe
  // computar isso (não usa valor_atual/qtde_usuario_atual/reajustes/expansoes, que ficam vazios/
  // default nesses itens). Os demais itens continuam calculados 100% no cliente, como sempre.
  const itensComCalculo = useMemo(() => calcularItensComCalculo(itens), [itens]);
  // Só para o card "Orçado {ano-1}"/sparkline comparativo — não entra na árvore de categorias
  // (essa continua olhando só para o ano selecionado).
  const itensAnoAnteriorComCalculo = useMemo(() => calcularItensComCalculo(itensAnoAnterior), [itensAnoAnterior]);
  const projecaoMensalAnoAnterior = useMemo(
    () => somarProjecoes(itensAnoAnteriorComCalculo.map((i) => i.projecao)),
    [itensAnoAnteriorComCalculo]
  );
  const totalProjetadoAnoAnterior = calcTotalAnual(projecaoMensalAnoAnterior);

  const categoriaPorId = useMemo(() => Object.fromEntries(categorias.map((c) => [c.id, c])), [categorias]);

  // Hierarquia de 2 níveis: Categoria Pai (categoria_pai_id nulo) > Subcategoria (aponta pra pai).
  // Itens só existem dentro de subcategorias — a categoria pai nunca tem itens diretos.
  const categoriasOrdenadas = useMemo(
    () => [...categorias].sort((a, b) => (a.ordem - b.ordem) || a.codigo.localeCompare(b.codigo)),
    [categorias]
  );

  const categoriasTopoOrdenadas = useMemo(
    () => categoriasOrdenadas.filter((c) => !c.categoria_pai_id),
    [categoriasOrdenadas]
  );

  const subcategoriasOrdenadas = useMemo(
    () => categoriasOrdenadas.filter((c) => c.categoria_pai_id),
    [categoriasOrdenadas]
  );

  const arvoreCategorias = useMemo(() => categoriasTopoOrdenadas.map((categoriaPai) => {
    const subcategorias = subcategoriasOrdenadas
      .filter((s) => s.categoria_pai_id === categoriaPai.id)
      .map((sub) => {
        const itensSub = itensComCalculo
          .filter((i) => i.categoria_id === sub.id)
          .sort((a, b) => a.nome.localeCompare(b.nome));
        const projecao = somarProjecoes(itensSub.map((i) => i.projecao));
        const valorAtual = itensSub.reduce((acc, i) => acc + Number(i.valorBase || 0), 0);
        return { categoria: sub, itens: itensSub, projecao, total: calcTotalAnual(projecao), valorAtual };
      });
    const projecao = somarProjecoes(subcategorias.map((s) => s.projecao));
    const valorAtual = subcategorias.reduce((acc, s) => acc + s.valorAtual, 0);
    return { categoria: categoriaPai, subcategorias, projecao, total: calcTotalAnual(projecao), valorAtual };
  }), [categoriasTopoOrdenadas, subcategoriasOrdenadas, itensComCalculo]);

  const totalGeralProjecao = useMemo(() => somarProjecoes(arvoreCategorias.map((l) => l.projecao)), [arvoreCategorias]);
  const totalOrcadoBase = itensComCalculo.reduce((acc, i) => acc + Number(i.valorBase || 0), 0);
  const totalProjetadoAno = calcTotalAnual(totalGeralProjecao);

  // Progresso Orçamentário: % de categorias mãe já marcadas como concluídas pelo próprio
  // usuário (checkbox no botão-KPI de cada categoria) — não é calculado a partir de valores,
  // é um flag manual (ver alternarStatusCategoria acima e OrcamentoCategoriaStatus no backend).
  const statusPorCategoriaId = useMemo(
    () => Object.fromEntries((statusCategorias || []).map((s) => [s.categoria_id, s.concluido])),
    [statusCategorias]
  );
  const totalCategoriasPai = categoriasTopoOrdenadas.length;
  const categoriasConcluidas = categoriasTopoOrdenadas.filter((c) => statusPorCategoriaId[c.id]).length;
  const percentProgresso = totalCategoriasPai > 0 ? (categoriasConcluidas / totalCategoriasPai) * 100 : 0;

  const variacaoAnual = totalProjetadoAno - totalProjetadoAnoAnterior;
  const direcaoVariacao = variacaoAnual > 0 ? "up" : variacaoAnual < 0 ? "down" : "flat";
  const percentVariacao = totalProjetadoAnoAnterior > 0 ? (variacaoAnual / totalProjetadoAnoAnterior) * 100 : 0;

  const kpiItemsOrcamento = [
    {
      label: `Orçado ${ano}`,
      value: loading ? "—" : formatCurrency(totalProjetadoAno),
      background: C.brand,
      accessory: !loading && totalGeralProjecao.some((v) => v) && <Sparkline data={totalGeralProjecao} color="#fff" />,
    },
    {
      label: `Orçado ${ano - 1}`,
      value: loading ? "—" : formatCurrency(totalProjetadoAnoAnterior),
      color: C.muted,
      accessory: !loading && projecaoMensalAnoAnterior.some((v) => v) && <Sparkline data={projecaoMensalAnoAnterior} color={C.muted} />,
    },
    {
      label: "Progresso Orçamentário",
      value: loading ? "—" : `${categoriasConcluidas}/${totalCategoriasPai}`,
      sub: "categorias mãe concluídas",
      accessory: !loading && <GaugeProgresso percent={percentProgresso} color={C.brand} trackColor={C.border} />,
    },
    {
      label: "Variação Anual",
      value: loading ? "—" : `${variacaoAnual > 0 ? "+" : ""}${formatCurrency(variacaoAnual)}`,
      color: loading ? C.text : direcaoVariacao === "up" ? C.success : direcaoVariacao === "down" ? C.danger : C.text,
      sub: loading ? "" : `vs. ${ano - 1}: ${variacaoAnual === 0 ? "Sem alteração" : `${percentVariacao > 0 ? "+" : ""}${percentVariacao.toFixed(1)}%`}`,
      accessory: !loading && <TrendAccessory direcao={direcaoVariacao} C={C} />,
    },
  ];

  // ── Exportar para Excel (backup/consulta caso o BD apresente problema) ────
  function exportarParaExcel() {
    const linhas = [];
    const linhaBase = (codigo, item, valorAtual, projecao, total, extra = {}) => ({
      "Código": codigo,
      "Item": item,
      "Valor Atual": valorAtual,
      "Vig. Valor Início": "",
      "Vig. Valor Fim": "",
      "Qtde Atual": "",
      "Valor Unit.": "",
      "Expansões": "",
      "Reajustes": "",
      ...extra,
      ...Object.fromEntries(MESES_ABREV.map((m, i) => [m, projecao[i]])),
      "Total": total,
    });

    arvoreCategorias.forEach((paiLinha) => {
      linhas.push(linhaBase(paiLinha.categoria.codigo, paiLinha.categoria.nome, paiLinha.valorAtual, paiLinha.projecao, paiLinha.total));
      paiLinha.subcategorias.forEach((subLinha) => {
        linhas.push(linhaBase(subLinha.categoria.codigo, `  ${subLinha.categoria.nome}`, subLinha.valorAtual, subLinha.projecao, subLinha.total));
        subLinha.itens.forEach((item) => {
          linhas.push(linhaBase("", `    ${item.nome}`, item.valorBase, item.projecao, item.total, {
            "Vig. Valor Início": item.mes_inicio_valor ? MESES_ABREV[item.mes_inicio_valor - 1] : "",
            "Vig. Valor Fim": item.mes_fim_valor ? MESES_ABREV[item.mes_fim_valor - 1] : "",
            "Qtde Atual": item.qtde_usuario_atual,
            "Valor Unit.": item.valorUnitario,
            "Expansões": resumoExpansoes(item.expansoes),
            "Reajustes": resumoReajustes(item.reajustes),
          }));
        });
      });
    });
    linhas.push(linhaBase("", "Total Geral", totalOrcadoBase, totalGeralProjecao, totalProjetadoAno));

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, tituloModulo.slice(0, 31));
    const nomeArquivo = `${tituloModulo}_${departamento}_${ano}`.replace(/[^\w]+/g, "_");
    XLSX.writeFile(workbook, `${nomeArquivo}.xlsx`);
  }

  // ── Modal: Categoria ──────────────────────────────────────────────────────
  function abrirModalCategoria(categoria = null, categoriaPaiIdPadrao = null) {
    setErroFormCategoria("");
    if (categoria) {
      setEditandoCategoria(categoria);
      setFormCategoria({
        codigo: categoria.codigo, nome: categoria.nome,
        categoria_pai_id: categoria.categoria_pai_id ? String(categoria.categoria_pai_id) : "",
        ordem: String(categoria.ordem ?? 0),
      });
    } else {
      setEditandoCategoria(null);
      setFormCategoria({ ...FORM_CATEGORIA_VAZIO, categoria_pai_id: categoriaPaiIdPadrao ? String(categoriaPaiIdPadrao) : "" });
    }
    setModalCategoriaAberto(true);
  }

  async function salvarCategoria() {
    const codigo = formCategoria.codigo.trim();
    const nome = formCategoria.nome.trim();
    const ordem = parseInt(formCategoria.ordem, 10);
    const categoriaPaiId = formCategoria.categoria_pai_id ? parseInt(formCategoria.categoria_pai_id, 10) : null;
    if (!codigo) return setErroFormCategoria("Informe o código.");
    if (!nome) return setErroFormCategoria("Informe o nome.");

    setSalvandoCategoria(true);
    setErroFormCategoria("");
    try {
      const payload = { codigo, nome, departamento, categoria_pai_id: categoriaPaiId, ordem: Number.isNaN(ordem) ? 0 : ordem };
      if (editandoCategoria) await putCategoriaOrcamento(editandoCategoria.id, payload);
      else await postCategoriaOrcamento(payload);
      setModalCategoriaAberto(false);
      await carregarTudo(ano, departamento);
    } catch (e) {
      setErroFormCategoria(e.message);
    } finally {
      setSalvandoCategoria(false);
    }
  }

  async function excluirCategoria(categoria) {
    if (!categoria) return;
    if (!window.confirm(`Excluir "${categoria.codigo} ${categoria.nome}"? Só é possível se não houver itens/subcategorias vinculados.`)) return;
    try {
      await deleteCategoriaOrcamento(categoria.id);
      await carregarTudo(ano, departamento);
    } catch (e) {
      setErro(e.message);
    }
  }

  // ── Modal: Item (Despesa/Custo Geral) ─────────────────────────────────────
  function abrirModalItem(item = null, categoriaIdPadrao = null) {
    setErroFormItem("");
    if (item) {
      setEditandoItem(item);
      setFormItem({
        categoria_id: String(item.categoria_id),
        nome: item.nome,
        valor_atual: String(item.valor_atual),
        mes_inicio_valor: item.mes_inicio_valor ? String(item.mes_inicio_valor) : "",
        mes_fim_valor: item.mes_fim_valor ? String(item.mes_fim_valor) : "",
        qtde_usuario_atual: String(item.qtde_usuario_atual),
        reajustes: (item.reajustes || []).map((r) => ({
          tipo_indice: r.tipo_indice, mes: String(r.mes), percentual: String(r.percentual),
        })),
        expansoes: (item.expansoes || []).map((e) => ({
          qtde_usuario_novo: String(e.qtde_usuario_novo), mes_inicio: String(e.mes_inicio), mes_fim: String(e.mes_fim),
          valor_usuario_novo: e.valor_usuario_novo !== null && e.valor_usuario_novo !== undefined ? String(e.valor_usuario_novo) : "",
        })),
      });
    } else {
      setEditandoItem(null);
      setFormItem({ ...FORM_ITEM_VAZIO, categoria_id: categoriaIdPadrao ? String(categoriaIdPadrao) : "" });
    }
    setModalItemAberto(true);
  }

  function adicionarReajuste() {
    setFormItem((f) => ({ ...f, reajustes: [...(f.reajustes || []), { tipo_indice: "", mes: "", percentual: "" }] }));
  }

  function atualizarReajuste(indice, campo, valor) {
    setFormItem((f) => {
      const reajustes = [...(f.reajustes || [])];
      reajustes[indice] = { ...reajustes[indice], [campo]: valor };
      return { ...f, reajustes };
    });
  }

  function removerReajuste(indice) {
    setFormItem((f) => ({ ...f, reajustes: (f.reajustes || []).filter((_, i) => i !== indice) }));
  }

  function adicionarExpansao() {
    setFormItem((f) => ({ ...f, expansoes: [...(f.expansoes || []), { qtde_usuario_novo: "", mes_inicio: "", mes_fim: "", valor_usuario_novo: "" }] }));
  }

  function atualizarExpansao(indice, campo, valor) {
    setFormItem((f) => {
      const expansoes = [...(f.expansoes || [])];
      expansoes[indice] = { ...expansoes[indice], [campo]: valor };
      return { ...f, expansoes };
    });
  }

  function removerExpansao(indice) {
    setFormItem((f) => ({ ...f, expansoes: (f.expansoes || []).filter((_, i) => i !== indice) }));
  }

  async function salvarItem() {
    const categoriaId = parseInt(formItem.categoria_id, 10);
    const nome = formItem.nome.trim();
    const valorAtual = parseFloat(formItem.valor_atual);
    const mesInicioValor = formItem.mes_inicio_valor ? parseInt(formItem.mes_inicio_valor, 10) : null;
    const mesFimValor = formItem.mes_fim_valor ? parseInt(formItem.mes_fim_valor, 10) : null;
    const qtdeAtual = parseInt(formItem.qtde_usuario_atual, 10);

    if (!categoriaId) return setErroFormItem("Selecione a categoria.");
    if (!nome) return setErroFormItem("Informe o nome do item.");
    if (Number.isNaN(valorAtual) || valorAtual < 0) return setErroFormItem("Informe um Valor Atual válido.");
    if (Boolean(mesInicioValor) !== Boolean(mesFimValor)) return setErroFormItem("Informe Mês Início e Mês Fim do Valor Atual juntos.");
    if (mesInicioValor && mesFimValor && mesFimValor < mesInicioValor) return setErroFormItem("Mês Fim do Valor Atual não pode ser anterior ao Mês Início.");
    if (Number.isNaN(qtdeAtual) || qtdeAtual < 0) return setErroFormItem("Informe uma Qtde Usuário Atual válida.");
    const reajusteIncompleto = (formItem.reajustes || []).some(
      (r) => !r.tipo_indice || !r.mes || r.percentual === "" || Number.isNaN(parseFloat(r.percentual))
    );
    if (reajusteIncompleto) {
      return setErroFormItem("Preencha Tipo, Mês e Percentual em todos os reajustes (ou remova a linha incompleta).");
    }
    const expansaoIncompleta = (formItem.expansoes || []).some(
      (e) => e.qtde_usuario_novo === "" || Number.isNaN(parseInt(e.qtde_usuario_novo, 10)) || !e.mes_inicio || !e.mes_fim
    );
    if (expansaoIncompleta) {
      return setErroFormItem("Preencha Nova Qtde, Mês Início e Mês Fim em todas as expansões (ou remova a linha incompleta).");
    }
    const expansaoMesInvalido = (formItem.expansoes || []).some(
      (e) => e.mes_inicio && e.mes_fim && parseInt(e.mes_fim, 10) < parseInt(e.mes_inicio, 10)
    );
    if (expansaoMesInvalido) {
      return setErroFormItem("Em uma expansão, o Mês Fim não pode ser anterior ao Mês Início.");
    }
    const expansaoValorInvalido = (formItem.expansoes || []).some(
      (e) => e.valor_usuario_novo !== "" && (Number.isNaN(parseFloat(e.valor_usuario_novo)) || parseFloat(e.valor_usuario_novo) < 0)
    );
    if (expansaoValorInvalido) {
      return setErroFormItem("Em uma expansão, o Novo Valor por Usuário, se informado, deve ser um número válido e não negativo.");
    }

    setSalvandoItem(true);
    setErroFormItem("");
    try {
      const payload = {
        categoria_id: categoriaId, nome, ano, departamento,
        valor_atual: valorAtual, mes_inicio_valor: mesInicioValor, mes_fim_valor: mesFimValor,
        qtde_usuario_atual: qtdeAtual,
        reajustes: reajustesDoForm(formItem),
        expansoes: expansoesDoForm(formItem),
      };
      if (editandoItem) await putDespesaGeral(editandoItem.id, payload);
      else await postDespesaGeral(payload);
      setModalItemAberto(false);
      await carregarTudo(ano, departamento);
    } catch (e) {
      setErroFormItem(e.message);
    } finally {
      setSalvandoItem(false);
    }
  }

  async function excluirItem(item) {
    if (!item) return;
    if (!window.confirm(`Excluir o item "${item.nome}"?`)) return;
    try {
      await deleteDespesaGeral(item.id);
      if (selectedItemId === item.id) setSelectedItemId(null);
      await carregarTudo(ano, departamento);
    } catch (e) {
      setErro(e.message);
    }
  }

  // ── Lançar Viagem (grupo) ──────────────────────────────────────────────────
  // Um item com item_grupo_id é sempre editado/excluído em grupo (afeta todas as subcategorias
  // do lançamento de uma vez), nunca item a item — mesmo padrão de Custos Gerais por Projeto (ver
  // abrirEdicaoItem/excluirItemOuGrupo em CustosGeraisProjetoView.js). item_grupo_id (não
  // tipo_lancamento) é o discriminador: a linha opcional de Verba de Relacionamento também
  // pertence ao grupo mas é modelo_valor='faixas'/tipo_lancamento='valor_manual' (não usa ticket).
  function abrirItemOuGrupo(item) {
    if (item.item_grupo_id) {
      const itensGrupo = itens.filter((i) => i.item_grupo_id === item.item_grupo_id);
      if (itensGrupo.some((i) => i.tipo_lancamento === "kit_admissao")) {
        const notebook = itensGrupo.find((i) => i.equipamento_codigo?.startsWith("NOTEBOOK_"));
        const tipoKit = notebook?.equipamento_codigo === "NOTEBOOK_AVANCADO" ? "avancado" : "basico";
        const faixa = notebook?.faixas_padrao?.[0];
        const nomeBase = notebook ? notebook.nome.replace(/ — .*$/, "") : "";
        setModalGrupoKitAdmissao({
          grupoExistente: {
            item_grupo_id: item.item_grupo_id, item_nome: nomeBase,
            tipo_kit: tipoKit, mes: faixa?.mes_inicio || 1, quantidade: faixa?.usuarios || 1,
          },
        });
        return;
      }
      const itemTicket = itensGrupo.find((i) => i.tipo_lancamento === "viagem") || item;
      const itemVerba = itensGrupo.find((i) => categoriaPorId[i.categoria_id]?.codigo === CODIGO_CATEGORIA_VERBA_RELACIONAMENTO);
      const temPassagemAerea = itensGrupo.some((i) => categoriaPorId[i.categoria_id]?.codigo === CODIGO_CATEGORIA_PASSAGEM_AEREA);
      const faixas = (itemTicket.faixas_viagem || []).map((f, idx) => ({ ...f, verba_relacionamento: itemVerba?.faixas_padrao?.[idx]?.valor ?? 0 }));
      setModalGrupoViagem({
        grupoExistente: {
          item_grupo_id: item.item_grupo_id, item_nome: itemTicket.nome, faixas,
          incluirPassagemAerea: temPassagemAerea,
          incluirVerbaRelacionamento: !!itemVerba,
        },
      });
    } else if (item.modelo_valor === "faixas") {
      setModalItemSimples({ itemExistente: item });
    } else {
      abrirModalItem(item);
    }
  }

  async function excluirGrupoViagem(item) {
    if (!window.confirm(`Excluir o lançamento de viagem "${item.nome}"? Isso remove o custo em todas as subcategorias vinculadas (Alimentação, Hospedagem, Passagem Aérea, Transporte e/ou Verba de Relacionamento). Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteGrupoViagemDespesaGeral(item.item_grupo_id);
      await carregarTudo(ano, departamento);
    } catch (e) {
      setErro(e.message);
    }
  }

  async function excluirGrupoKitAdmissao(item) {
    if (!window.confirm(`Excluir o Kit Admissão "${item.nome.replace(/ — .*$/, "")}"? Isso remove as 5 linhas do kit (Fone, Teclado, Mouse, Notebook e Monitor). Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteGrupoKitAdmissaoDespesaGeral(item.item_grupo_id);
      await carregarTudo(ano, departamento);
    } catch (e) {
      setErro(e.message);
    }
  }

  function excluirItemOuGrupo(item) {
    if (item.item_grupo_id) {
      return item.tipo_lancamento === "kit_admissao" ? excluirGrupoKitAdmissao(item) : excluirGrupoViagem(item);
    }
    return excluirItem(item);
  }

  // Erro de API sobe para o catch local do ModalGrupoViagemDespesa.salvar, não é engolido aqui.
  async function criarGrupoViagem(dados) {
    await postGrupoViagemDespesaGeral(dados);
    setModalGrupoViagem(null);
    await carregarTudo(ano, departamento);
  }

  async function atualizarGrupoViagem(itemGrupoId, dados) {
    await putGrupoViagemDespesaGeral(itemGrupoId, dados);
    setModalGrupoViagem(null);
    await carregarTudo(ano, departamento);
  }

  // Erro de API sobe para o catch local do ModalGrupoKitAdmissaoDespesa.salvar, não é engolido aqui.
  async function criarGrupoKitAdmissao(dados) {
    await postGrupoKitAdmissaoDespesaGeral(dados);
    setModalGrupoKitAdmissao(null);
    await carregarTudo(ano, departamento);
  }

  async function atualizarGrupoKitAdmissao(itemGrupoId, dados) {
    await putGrupoKitAdmissaoDespesaGeral(itemGrupoId, dados);
    setModalGrupoKitAdmissao(null);
    await carregarTudo(ano, departamento);
  }

  // ── Novo modal simples de item (modelo_valor='faixas') ──────────────────────
  // Erro de API sobe para o catch local do ModalNovoItemSimples.salvar, não é engolido aqui.
  async function criarItemSimples(dados) {
    await postDespesaGeral(dados);
    setModalItemSimples(null);
    await carregarTudo(ano, departamento);
  }

  async function atualizarItemSimples(itemId, dados) {
    await putDespesaGeral(itemId, dados);
    setModalItemSimples(null);
    await carregarTudo(ano, departamento);
  }

  const itemPreview = construirItemPreview(formItem);
  const previewBase = calcValorMensalBase(itemPreview);
  const previewProjecao = calcProjecaoMensal(itemPreview);
  const previewTotal = calcTotalAnual(previewProjecao);

  // Código + Item + Valor Atual + 12 meses + Total + Ações (Vig.Início/Fim, Qtde Atual, Valor
  // Unit., Expansões e Reajustes saíram só da visão da tabela — continuam no modal/Excel).
  const COLSPAN_TOTAL = 17;

  if (!departamentos || departamentos.length === 0) {
    return (
      <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
        <SideBarNavGestaoOrcamentaria isLight={isLight} />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 14 }}>
          Nenhum departamento disponível para o seu perfil em {tituloModulo}.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria
        isLight={isLight}
        departamentos={departamentos}
        departamentoAtual={departamento}
        onSelecionarDepartamento={setDepartamento}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· {tituloModulo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={departamento}
              onChange={(e) => setDepartamento(e.target.value)}
              disabled={departamentos.length <= 1}
              title={departamento}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600, maxWidth: 220, cursor: departamentos.length <= 1 ? "default" : "pointer" }}
            >
              {departamentos.map((d) => <option key={d} value={d} style={{ color: "#0F172A" }}>{d}</option>)}
            </select>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          {/* KPIs */}
          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <KpiRowOrcamento items={kpiItemsOrcamento} C={C} />
          </section>

          {/* Lançar por categoria — botões cheios (1 por categoria mãe, + 1 genérico "Lançar
              Despesa" no início); ao clicar, escolhe a subcategoria e abre o modal de item, em
              vez de editar/criar direto na tabela. Cada botão de categoria também traz um
              checkbox "concluído" no canto — flag manual de progresso, ver alternarStatusCategoria. */}
          {!loading && categoriasTopoOrdenadas.length > 0 && (
            <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Lançar Gastos</h2>
              <BotoesCategoriaLancamento
                arvoreCategorias={arvoreCategorias}
                onSelecionar={setEscolhaCategoriaPai}
                onLancarGenerico={() => setEscolhaGenericaAberta(true)}
                statusPorCategoriaId={statusPorCategoriaId}
                onToggleStatus={alternarStatusCategoria}
                C={C}
              />
            </section>
          )}

          {/* Tabela principal */}
          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
                 {departamento} · {tituloModulo}  (CONSOLIDADO)
              </h2>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={exportarParaExcel}
                  disabled={loading || categoriasTopoOrdenadas.length === 0}
                  title="Exportar dados para Excel"
                  style={{ ...btnStyle(C.success, true), opacity: loading || categoriasTopoOrdenadas.length === 0 ? 0.45 : 1, cursor: loading || categoriasTopoOrdenadas.length === 0 ? "not-allowed" : "pointer" }}
                >
                  <FileSpreadsheet size={15} /> Exportar Excel
                </button>
                <button onClick={() => setGerenciarCategoriasAberto(true)} style={btnStyle(C.brand, true)}>
                  <Settings2 size={15} /> Gerenciar Categorias
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : categoriasTopoOrdenadas.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhuma categoria cadastrada para {departamento}. Clique em "Nova Categoria" para começar.
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1800 }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle(C), position: "sticky", left: 0, zIndex: 4, width: COL_ACOES_WIDTH, minWidth: COL_ACOES_WIDTH }}>Ações</th>
                        <th style={{ ...thStyle(C), position: "sticky", left: COL_ACOES_WIDTH, zIndex: 4, width: COL_CODIGO_WIDTH, minWidth: COL_CODIGO_WIDTH }}>Código</th>
                        <th style={{ ...thStyle(C), textAlign: "left", position: "sticky", left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, zIndex: 4, width: COL_ITEM_WIDTH, minWidth: COL_ITEM_WIDTH }}>Item</th>
                        <th style={thStyle(C)}>Valor Atual</th>
                        {MESES_ABREV.map((m) => <th key={m} style={thStyle(C)}>{m}</th>)}
                        <th style={thStyle(C)}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arvoreCategorias.map((paiLinha) => {
                        const paiColapsada = categoriasColapsadas.has(paiLinha.categoria.id);
                        return (
                        <Fragment key={paiLinha.categoria.id}>
                          <tr
                            onClick={() => toggleCategoria(paiLinha.categoria.id)}
                            style={{ background: C.categoriaBg, fontWeight: 700, cursor: "pointer" }}
                          >
                            <td style={tdStyle(C, true, { left: 0, background: C.categoriaBg })} />
                            <td style={tdStyle(C, true, { left: COL_ACOES_WIDTH, background: C.categoriaBg })}>
                              <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start" }}>
                                {paiColapsada ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                {paiLinha.categoria.codigo}
                              </div>
                            </td>
                            <td style={{ ...tdStyle(C, true, { left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, background: C.categoriaBg }), textAlign: "left" }}>
                              {paiLinha.categoria.nome}
                              {paiColapsada && (
                                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                                  ({paiLinha.subcategorias.length} {paiLinha.subcategorias.length === 1 ? "subcategoria" : "subcategorias"})
                                </span>
                              )}
                            </td>
                            <td style={tdStyle(C)}>{formatCurrency(paiLinha.valorAtual)}</td>
                            {paiLinha.projecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                            <td style={tdStyle(C)}>{formatCurrency(paiLinha.total)}</td>
                          </tr>

                          {!paiColapsada && paiLinha.subcategorias.length === 0 && (
                            <tr>
                              <td colSpan={COLSPAN_TOTAL} style={{ padding: "8px 12px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                                Nenhuma subcategoria cadastrada nesta categoria.
                              </td>
                            </tr>
                          )}

                          {!paiColapsada && paiLinha.subcategorias.map((subLinha) => {
                            const subColapsada = categoriasColapsadas.has(subLinha.categoria.id);
                            return (
                            <Fragment key={subLinha.categoria.id}>
                              <tr
                                onClick={() => toggleCategoria(subLinha.categoria.id)}
                                style={{ background: C.subcategoriaBg, fontWeight: 600, cursor: "pointer" }}
                              >
                                <td style={tdStyle(C, true, { left: 0, background: C.subcategoriaBg })} />
                                <td style={tdStyle(C, true, { left: COL_ACOES_WIDTH, background: C.subcategoriaBg })}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start", paddingLeft: 16 }}>
                                    {subColapsada ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                                    {subLinha.categoria.codigo}
                                  </div>
                                </td>
                                <td style={{ ...tdStyle(C, true, { left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, background: C.subcategoriaBg }), textAlign: "left" }}>
                                  {subLinha.categoria.nome}
                                  {subColapsada && (
                                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                                      ({subLinha.itens.length} {subLinha.itens.length === 1 ? "item" : "itens"})
                                    </span>
                                  )}
                                </td>
                                <td style={tdStyle(C)}>{formatCurrency(subLinha.valorAtual)}</td>
                                {subLinha.projecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                                <td style={tdStyle(C)}>{formatCurrency(subLinha.total)}</td>
                              </tr>

                              {!subColapsada && subLinha.itens.length === 0 && (
                                <tr>
                                  <td colSpan={COLSPAN_TOTAL} style={{ padding: "8px 12px 8px 40px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                                    Nenhum item cadastrado nesta subcategoria.
                                  </td>
                                </tr>
                              )}

                              {!subColapsada && subLinha.itens.map((item) => {
                                const ehViagem = item.tipo_lancamento === "viagem";
                                const ehKit = item.tipo_lancamento === "kit_admissao";
                                return (
                                <tr
                                  key={item.id}
                                  onClick={() => setSelectedItemId(item.id === selectedItemId ? null : item.id)}
                                  style={{ background: item.id === selectedItemId ? C.bgActive : "transparent", cursor: "pointer" }}
                                >
                                  <td
                                    style={tdStyle(C, false, { left: 0, background: item.id === selectedItemId ? C.bgActive : C.card })}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                      <IconBtn title={ehViagem ? "Editar lançamento de viagem (afeta todas as subcategorias)" : ehKit ? "Editar Kit Admissão (afeta as 5 linhas)" : "Editar item"} onClick={() => abrirItemOuGrupo(item)} C={C}><Pencil size={13} /></IconBtn>
                                      <IconBtn title={ehViagem ? "Excluir lançamento de viagem (afeta todas as subcategorias)" : ehKit ? "Excluir Kit Admissão (afeta as 5 linhas)" : "Excluir item"} onClick={() => excluirItemOuGrupo(item)} C={C} danger><Trash2 size={13} /></IconBtn>
                                    </div>
                                  </td>
                                  <td style={tdStyle(C, false, { left: COL_ACOES_WIDTH, background: item.id === selectedItemId ? C.bgActive : C.card })}>—</td>
                                  <td style={{ ...tdStyle(C, false, { left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, background: item.id === selectedItemId ? C.bgActive : C.card }), textAlign: "left" }}>
                                    {item.nome}
                                    {ehViagem && <Plane size={11} color={C.muted} style={{ marginLeft: 6, verticalAlign: "middle" }} />}
                                    {ehKit && <Laptop size={11} color={C.muted} style={{ marginLeft: 6, verticalAlign: "middle" }} />}
                                  </td>
                                  {ehViagem || ehKit ? (
                                    <td style={tdStyle(C)} />
                                  ) : (
                                    <td style={tdStyle(C)}>{formatCurrency(item.valorBase)}</td>
                                  )}
                                  {item.projecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                                  <td style={{ ...tdStyle(C), fontWeight: 700 }}>{formatCurrency(item.total)}</td>
                                </tr>
                                );
                              })}
                            </Fragment>
                            );
                          })}
                        </Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                        <td style={tdStyle(C, true, { left: 0, background: C.card })} />
                        <td style={{ ...tdStyle(C, true, { left: COL_ACOES_WIDTH, background: C.card }), textAlign: "left" }} colSpan={2}>Total Geral</td>
                        <td style={tdStyle(C)}>{formatCurrency(totalOrcadoBase)}</td>
                        {totalGeralProjecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                        <td style={tdStyle(C)}>{formatCurrency(totalProjetadoAno)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>


      {/* Modal: Gerenciar Categorias — lista + ações; abre o form de categoria (abaixo) por cima */}
      {gerenciarCategoriasAberto && (
        <ModalGerenciarCategorias
          C={C}
          categoriasTopo={categoriasTopoOrdenadas}
          subcategorias={subcategoriasOrdenadas}
          onClose={() => setGerenciarCategoriasAberto(false)}
          onNovaCategoria={() => abrirModalCategoria(null)}
          onNovaSubcategoria={(paiId) => abrirModalCategoria(null, paiId)}
          onEditar={abrirModalCategoria}
          onExcluir={excluirCategoria}
        />
      )}

      {/* Modal: Categoria */}
      {modalCategoriaAberto && (
        <ModalShell C={C} title={editandoCategoria ? "Editar Categoria" : "Nova Categoria"} onClose={() => setModalCategoriaAberto(false)} maxWidth={420}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Departamento: <strong style={{ color: C.text }}>{departamento}</strong></p>
            <Campo label="Categoria Pai" C={C}>
              <select style={inputStyle(C)} value={formCategoria.categoria_pai_id}
                onChange={(e) => setFormCategoria({ ...formCategoria, categoria_pai_id: e.target.value })}>
                <option value="">— Nenhuma (categoria de nível 1) —</option>
                {categoriasTopoOrdenadas
                  .filter((c) => !editandoCategoria || c.id !== editandoCategoria.id)
                  .map((c) => <option key={c.id} value={c.id}>{c.codigo} {c.nome}</option>)}
              </select>
            </Campo>
            <p style={{ margin: "-6px 0 0", fontSize: 11, color: C.muted }}>
              Deixe em branco para uma categoria pai (ex: 2.3). Selecione uma categoria pai para criar uma subcategoria dela (ex: 2.3.01) — só as subcategorias recebem itens.
            </p>
            <Campo label="Código" C={C}>
              <input style={inputStyle(C)} type="text" placeholder="Ex: 2.3" value={formCategoria.codigo}
                onChange={(e) => setFormCategoria({ ...formCategoria, codigo: e.target.value })} />
            </Campo>
            <Campo label="Nome" C={C}>
              <input style={inputStyle(C)} type="text" placeholder="Ex: Serviços Terceirizados" value={formCategoria.nome}
                onChange={(e) => setFormCategoria({ ...formCategoria, nome: e.target.value })} />
            </Campo>
            <Campo label="Ordem de Exibição" C={C}>
              <input style={inputStyle(C)} type="number" step="1" value={formCategoria.ordem}
                onChange={(e) => setFormCategoria({ ...formCategoria, ordem: e.target.value })} />
            </Campo>
            {erroFormCategoria && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroFormCategoria}</p>}
          </div>
          <ModalFooter
            C={C} salvando={salvandoCategoria}
            onCancel={() => setModalCategoriaAberto(false)}
            onSave={salvarCategoria}
            labelSalvar={editandoCategoria ? "Atualizar" : "Cadastrar"}
          />
        </ModalShell>
      )}

      {/* Modal: escolha de categoria mãe — aberto pelo botão genérico "Lançar Despesa"; ao
          escolher, cai no mesmo ModalEscolherSubcategoria de sempre. */}
      {escolhaGenericaAberta && (
        <ModalEscolherCategoriaPai
          C={C}
          arvoreCategorias={arvoreCategorias}
          onClose={() => setEscolhaGenericaAberta(false)}
          onEscolher={(paiLinha) => { setEscolhaGenericaAberta(false); setEscolhaCategoriaPai(paiLinha); }}
        />
      )}

      {/* Modal: escolha de subcategoria — aberto pelo botão-KPI da categoria mãe */}
      {escolhaCategoriaPai && (
        <ModalEscolherSubcategoria
          C={C}
          paiLinha={escolhaCategoriaPai}
          onClose={() => setEscolhaCategoriaPai(null)}
          onEscolher={(categoriaId) => {
            setEscolhaCategoriaPai(null);
            setModalItemSimples({ categoriaId });
          }}
          onLancarViagem={() => { setEscolhaCategoriaPai(null); setModalGrupoViagem({}); }}
          onLancarKitAdmissao={() => { setEscolhaCategoriaPai(null); setModalGrupoKitAdmissao({}); }}
        />
      )}

      {/* Modal: Lançar/Editar Viagem (grupo) — mesmo mecanismo de Custos Gerais por Projeto */}
      {modalGrupoViagem && (
        <ModalGrupoViagemDespesa
          C={C}
          ano={ano}
          departamento={departamento}
          grupoExistente={modalGrupoViagem.grupoExistente}
          onClose={() => setModalGrupoViagem(null)}
          onCriar={criarGrupoViagem}
          onAtualizar={atualizarGrupoViagem}
        />
      )}

      {/* Modal: Lançar/Editar Kit Admissão (grupo) */}
      {modalGrupoKitAdmissao && (
        <ModalGrupoKitAdmissaoDespesa
          C={C}
          ano={ano}
          departamento={departamento}
          grupoExistente={modalGrupoKitAdmissao.grupoExistente}
          onClose={() => setModalGrupoKitAdmissao(null)}
          onCriar={criarGrupoKitAdmissao}
          onAtualizar={atualizarGrupoKitAdmissao}
        />
      )}

      {/* Modal: Item (Despesa/Custo Geral) */}
      {modalItemAberto && (
        <ModalShell C={C} title={editandoItem ? "Editar Item" : "Novo Item"} onClose={() => setModalItemAberto(false)} maxWidth={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Departamento: <strong style={{ color: C.text }}>{departamento}</strong></p>
            <Campo label="Subcategoria" C={C}>
              <ComboBoxFiltravel
                C={C}
                value={formItem.categoria_id}
                onChange={(v) => setFormItem({ ...formItem, categoria_id: v })}
                options={subcategoriasOrdenadas.map((c) => {
                  const pai = categorias.find((p) => p.id === c.categoria_pai_id);
                  return { value: c.id, label: `${c.codigo} ${c.nome}${pai ? ` — sob ${pai.codigo} ${pai.nome}` : ""}` };
                })}
              />
            </Campo>

            <Campo label="Nome do Item" C={C}>
              <input style={inputStyle(C)} type="text" value={formItem.nome}
                onChange={(e) => setFormItem({ ...formItem, nome: e.target.value })} />
            </Campo>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Campo label="Valor Atual (por usuário, R$)" C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" placeholder="0.00" value={formItem.valor_atual}
                  onChange={(e) => setFormItem({ ...formItem, valor_atual: e.target.value })} />
              </Campo>
              <Campo label="Qtde Usuário Atual" C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="1" value={formItem.qtde_usuario_atual}
                  onChange={(e) => setFormItem({ ...formItem, qtde_usuario_atual: e.target.value })} />
              </Campo>
            </div>
            <p style={{ margin: "-6px 0 0", fontSize: 11, color: C.muted }}>
              Valor Mensal Base (calculado, Valor Atual × Qtde Atual): {formatCurrency(previewBase)}
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Campo label="Mês Início (Valor Atual)" C={C}>
                <select style={inputStyle(C)} value={formItem.mes_inicio_valor}
                  onChange={(e) => setFormItem({ ...formItem, mes_inicio_valor: e.target.value })}>
                  <option value="">—</option>
                  {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Campo>
              <Campo label="Mês Fim (Valor Atual)" C={C}>
                <select style={inputStyle(C)} value={formItem.mes_fim_valor}
                  onChange={(e) => setFormItem({ ...formItem, mes_fim_valor: e.target.value })}>
                  <option value="">—</option>
                  {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Campo>
            </div>
            <p style={{ margin: "-6px 0 0", fontSize: 11, color: C.muted }}>
              Deixe em branco para o Valor Atual valer o ano inteiro. Preenchidos, o valor só entra na projeção
              dentro desse período (fora dele conta como R$ 0,00) — mesma lógica das janelas abaixo, para a Nova Qtde de Usuários.
            </p>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
                  Nova Qtde de Usuários
                </label>
                <button type="button" onClick={adicionarExpansao} style={{
                  display: "flex", alignItems: "center", gap: 4, background: "transparent",
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px",
                  color: C.brand, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                  <Plus size={12} /> Adicionar Expansão
                </button>
              </div>
              {(formItem.expansoes || []).length === 0 && (
                <p style={{ margin: 0, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                  Nenhuma expansão cadastrada — o item usa só a Qtde Atual o ano todo.
                </p>
              )}
              {(formItem.expansoes || []).map((e, i) => (
                <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                    <IconBtn title="Remover expansão" onClick={() => removerExpansao(i)} C={C} danger><Trash2 size={13} /></IconBtn>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <input style={inputStyle(C)} type="number" min="0" step="1" placeholder="Nova Qtde (Total)" value={e.qtde_usuario_novo}
                      onChange={(ev) => atualizarExpansao(i, "qtde_usuario_novo", ev.target.value)} />
                    <input style={inputStyle(C)} type="number" min="0" step="0.01" placeholder="Novo Valor/Usuário (opcional)" value={e.valor_usuario_novo}
                      onChange={(ev) => atualizarExpansao(i, "valor_usuario_novo", ev.target.value)} />
                    <select style={inputStyle(C)} value={e.mes_inicio} onChange={(ev) => atualizarExpansao(i, "mes_inicio", ev.target.value)}>
                      <option value="">Mês Início…</option>
                      {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select style={inputStyle(C)} value={e.mes_fim} onChange={(ev) => atualizarExpansao(i, "mes_fim", ev.target.value)}>
                      <option value="">Mês Fim…</option>
                      {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              ))}
              <p style={{ margin: "4px 0 0", fontSize: 11, color: C.muted }}>
                Nova Qtde é o total após a mudança (não o incremento). Fora das janelas cadastradas, a quantidade volta a ser a Qtde Atual.
                Novo Valor/Usuário é opcional — preencha só se o valor por usuário mudar junto com a quantidade (ex: desconto por volume);
                deixe em branco para manter o Valor Atual (já com os reajustes aplicados).
              </p>
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
                  Reajustes de Valor
                </label>
                <button type="button" onClick={adicionarReajuste} style={{
                  display: "flex", alignItems: "center", gap: 4, background: "transparent",
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px",
                  color: C.brand, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                  <Plus size={12} /> Adicionar Reajuste
                </button>
              </div>
              {(formItem.reajustes || []).length === 0 && (
                <p style={{ margin: 0, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                  Nenhum reajuste cadastrado — o item usa só o Valor Atual o ano todo.
                </p>
              )}
              {(formItem.reajustes || []).map((r, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, marginBottom: 8 }}>
                  <select style={inputStyle(C)} value={r.tipo_indice} onChange={(e) => atualizarReajuste(i, "tipo_indice", e.target.value)}>
                    <option value="">Tipo…</option>
                    <option value="inflacao">Inflação</option>
                    <option value="reajuste">Reajuste</option>
                  </select>
                  <select style={inputStyle(C)} value={r.mes} onChange={(e) => atualizarReajuste(i, "mes", e.target.value)}>
                    <option value="">Mês…</option>
                    {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <input style={inputStyle(C)} type="number" step="0.01" placeholder="% Ex: 4,00" value={r.percentual}
                    onChange={(e) => atualizarReajuste(i, "percentual", e.target.value)} />
                  <IconBtn title="Remover reajuste" onClick={() => removerReajuste(i)} C={C} danger><Trash2 size={13} /></IconBtn>
                </div>
              ))}
            </div>

            {erroFormItem && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroFormItem}</p>}

            {/* Pré-visualização da projeção mensal */}
            <div>
              <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Pré-visualização · Projeção Mensal
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 560 }}>
                  <thead>
                    <tr>
                      {MESES_ABREV.map((m) => <th key={m} style={thStyle(C)}>{m}</th>)}
                      <th style={thStyle(C)}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {previewProjecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                      <td style={{ ...tdStyle(C), fontWeight: 700 }}>{formatCurrency(previewTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <ModalFooter
            C={C} salvando={salvandoItem}
            onCancel={() => setModalItemAberto(false)}
            onSave={salvarItem}
            labelSalvar={editandoItem ? "Atualizar" : "Cadastrar"}
          />
        </ModalShell>
      )}

      {/* Modal: Novo Item simples (modelo_valor='faixas', padrão Custos Gerais por Projeto) —
          único fluxo de criação daqui em diante; itens 'legado' continuam editáveis só pelo
          modal rico acima (modalItemAberto). */}
      {modalItemSimples && (
        <ModalNovoItemSimples
          C={C}
          ano={ano}
          departamento={departamento}
          categoria={categoriaPorId[modalItemSimples.categoriaId || modalItemSimples.itemExistente?.categoria_id]}
          itemExistente={modalItemSimples.itemExistente}
          onClose={() => setModalItemSimples(null)}
          onCriar={criarItemSimples}
          onAtualizar={atualizarItemSimples}
        />
      )}
    </div>
  );
}

// ── Helpers de UI locais ─────────────────────────────────────────────────────

// Botões de ação, 1 por categoria mãe — clicar abre o seletor de subcategoria. Visual de botão
// clicável (ícone "+" em destaque, sem valor) e não de tile de KPI, para não confundir com os
// indicadores lá em cima. Substitui o antigo "+" direto em cada linha de subcategoria/o botão
// genérico "Novo Item".
// Botões cheios (fundo roxo sólido, não mais cartão branco com contorno) — 1 genérico
// "Lançar Despesa" (não sabe a categoria de antemão, abre o seletor de categoria mãe) seguido
// de 1 por categoria mãe. Cada botão de categoria carrega um checkbox "concluído" no canto —
// flag manual de progresso (statusPorCategoriaId/onToggleStatus), independente do clique
// principal do botão (por isso o stopPropagation no onClick do checkbox).
function BotoesCategoriaLancamento({ arvoreCategorias, onSelecionar, onLancarGenerico, statusPorCategoriaId, onToggleStatus, C }) {
  if (arvoreCategorias.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>

      {arvoreCategorias.map((paiLinha) => {
        const semSubcategorias = paiLinha.subcategorias.length === 0;
        const concluida = !!statusPorCategoriaId[paiLinha.categoria.id];
        return (
          <button
            key={paiLinha.categoria.id}
            onClick={() => onSelecionar(paiLinha)}
            disabled={semSubcategorias}
            title={semSubcategorias ? "Cadastre uma subcategoria primeiro" : `Lançar em ${paiLinha.categoria.nome}`}
            style={{
              position: "relative", display: "flex", alignItems: "center", gap: 10,
              background: concluida ? C.success : C.brand, border: "none", borderRadius: 10,
              padding: "12px 14px 12px 14px", cursor: semSubcategorias ? "not-allowed" : "pointer",
              opacity: semSubcategorias ? 0.5 : 1, textAlign: "left", fontFamily: "inherit",
              boxShadow: `0 2px 8px ${(concluida ? C.success : C.brand)}40`, transition: "background .15s",
            }}
          >
            <span style={{
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.22)", color: "#fff",
            }}>
              <Plus size={15} />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{paiLinha.categoria.codigo}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{paiLinha.categoria.nome}</span>
            </span>
            <input
              type="checkbox"
              checked={concluida}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggleStatus(paiLinha.categoria.id, e.target.checked)}
              title={concluida ? "Marcado como concluído — clique para desmarcar" : "Marcar como concluído (já terminei de preencher esta categoria)"}
              style={{ width: 16, height: 16, flexShrink: 0, accentColor: "#fff", cursor: "pointer" }}
            />
          </button>
        );
      })}
    </div>
  );
}

// Escolha da categoria mãe (aberta pelo botão genérico "Lançar Despesa") — depois cai no
// ModalEscolherSubcategoria de sempre, já com a categoria escolhida.
function ModalEscolherCategoriaPai({ C, arvoreCategorias, onClose, onEscolher }) {
  return (
    <ModalShell C={C} title="Lançar Despesa" onClose={onClose} maxWidth={480}>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: C.muted }}>Escolha a categoria mãe:</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, padding: 6 }}>
        {arvoreCategorias.map((paiLinha) => (
          <button
            key={paiLinha.categoria.id}
            type="button"
            onClick={() => onEscolher(paiLinha)}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
              background: "transparent", border: "1px solid transparent", borderRadius: 8,
              padding: "10px 12px", cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, minWidth: 48, flexShrink: 0 }}>{paiLinha.categoria.codigo}</span>
            <span style={{ fontSize: 13, color: C.text }}>{paiLinha.categoria.nome}</span>
          </button>
        ))}
      </div>
    </ModalShell>
  );
}

// Gerenciar Categorias — lista + ações (criar/editar/excluir categoria e subcategoria), mesmo
// padrão do "Gerenciar Projetos" em Custos Gerais por Projeto (ProjetosCrudModal): a tabela
// principal deixa de ter esses ícones, tudo fica dentro deste modal de gestão.
function ModalGerenciarCategorias({ C, categoriasTopo, subcategorias, onClose, onNovaCategoria, onNovaSubcategoria, onEditar, onExcluir }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 45, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Gerenciar Categorias</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>

        <button onClick={onNovaCategoria} style={{ ...btnStyle(C.brand), marginBottom: 16 }}>
          <FolderPlus size={15} /> Nova Categoria
        </button>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {categoriasTopo.length === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: C.muted, textAlign: "center", padding: 16 }}>Nenhuma categoria cadastrada.</p>
          )}
          {categoriasTopo.map((pai) => {
            const subs = subcategorias.filter((s) => s.categoria_pai_id === pai.id);
            return (
              <div key={pai.id} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.text }}>{pai.codigo} {pai.nome}</span>
                  <IconBtn title="Adicionar subcategoria" onClick={() => onNovaSubcategoria(pai.id)} C={C}><Plus size={13} /></IconBtn>
                  <IconBtn title="Editar categoria" onClick={() => onEditar(pai)} C={C}><Pencil size={13} /></IconBtn>
                  <IconBtn title="Excluir categoria" onClick={() => onExcluir(pai)} C={C} danger><Trash2 size={13} /></IconBtn>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8, paddingLeft: 14 }}>
                  {subs.length === 0 && (
                    <span style={{ fontSize: 11, fontStyle: "italic", color: C.muted }}>Nenhuma subcategoria cadastrada.</span>
                  )}
                  {subs.map((sub) => (
                    <div key={sub.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ flex: 1, fontSize: 12, color: C.text }}>{sub.codigo} {sub.nome}</span>
                      <IconBtn title="Editar subcategoria" onClick={() => onEditar(sub)} C={C}><Pencil size={12} /></IconBtn>
                      <IconBtn title="Excluir subcategoria" onClick={() => onExcluir(sub)} C={C} danger><Trash2 size={12} /></IconBtn>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Lista de subcategorias sempre visível (em vez de um combo fechado) — maior área de leitura;
// mesmo com scroll (muitas subcategorias), dá pra ver várias linhas de uma vez.
function ListaSubcategorias({ C, subcategorias, selecionadaId, onSelecionar }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, padding: 6 }}>
      {subcategorias.map((s) => {
        const selecionada = String(selecionadaId) === String(s.categoria.id);
        return (
          <button
            key={s.categoria.id}
            type="button"
            onClick={() => onSelecionar(s.categoria.id)}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
              background: selecionada ? C.bgActive : "transparent",
              border: `1px solid ${selecionada ? C.brand : "transparent"}`,
              borderRadius: 8, padding: "10px 12px", cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, minWidth: 48, flexShrink: 0 }}>{s.categoria.codigo}</span>
            <span style={{ fontSize: 13, color: C.text, fontWeight: selecionada ? 700 : 400 }}>{s.categoria.nome}</span>
          </button>
        );
      })}
    </div>
  );
}

// Modal intermediário: escolhe a subcategoria (filtrada à categoria mãe clicada) antes de abrir
// o modal de item de verdade — a regra de negócio do item em si não muda, só o caminho até ele.
// Para a categoria de Viagens, o único caminho é o atalho "Lançar Viagem" — as 5 subcategorias
// (2.5.01–05, incluindo Verba de Relacionamento) são todas cobertas pelo fluxo de grupo, então
// não há mais opção de lançar item avulso nessa categoria.
function ModalEscolherSubcategoria({ C, paiLinha, onClose, onEscolher, onLancarViagem, onLancarKitAdmissao }) {
  const ehViagens = paiLinha.categoria.codigo === CODIGO_CATEGORIA_VIAGENS_PREFIXO;
  const ehEquipamentos = paiLinha.categoria.codigo === CODIGO_CATEGORIA_EQUIPAMENTOS_PREFIXO;
  const [subcategoriaId, setSubcategoriaId] = useState(paiLinha.subcategorias[0]?.categoria.id ?? "");

  return (
    <ModalShell C={C} title={`Lançar em ${paiLinha.categoria.codigo} ${paiLinha.categoria.nome}`} onClose={onClose} maxWidth={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {ehViagens ? (
          <>
            <button onClick={onLancarViagem} style={{ ...btnStyle(C.brand), justifyContent: "center" }}>
              <Plane size={15} /> Lançar Viagem
            </button>
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
              Lança de uma vez o custo em Alimentação, Hospedagem, Passagem Aérea, Transporte e Verba de Relacionamento, usando o ticket médio travado do Backoffice (compartilhado por todos os departamentos de Despesas Gerais).
            </p>
          </>
        ) : ehEquipamentos ? (
          <>
            <button onClick={onLancarKitAdmissao} style={{ ...btnStyle(C.brand), justifyContent: "center" }}>
              <Laptop size={15} /> Lançar Kit Admissão
            </button>
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
              Lança de uma vez 1 Fone + 1 Teclado + 1 Mouse + 1 Notebook + 1 Monitor (Básico ou Avançado), usando o ticket cadastrado em Premissas · Equipamentos e Materiais.
            </p>
            {paiLinha.subcategorias.length > 0 && (
              <>
                <p style={{ margin: "6px 0 0", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Ou lance um item avulso:</p>
                <ListaSubcategorias C={C} subcategorias={paiLinha.subcategorias} selecionadaId={subcategoriaId} onSelecionar={setSubcategoriaId} />
                <button
                  style={{ ...btnStyle(C.brand, true), justifyContent: "center", opacity: subcategoriaId ? 1 : 0.5 }}
                  onClick={() => onEscolher(subcategoriaId)}
                  disabled={!subcategoriaId}
                >
                  Continuar
                </button>
              </>
            )}
          </>
        ) : paiLinha.subcategorias.length > 0 ? (
          <>
            <ListaSubcategorias C={C} subcategorias={paiLinha.subcategorias} selecionadaId={subcategoriaId} onSelecionar={setSubcategoriaId} />
            <button
              style={{ ...btnStyle(C.brand), justifyContent: "center", opacity: subcategoriaId ? 1 : 0.5 }}
              onClick={() => onEscolher(subcategoriaId)}
              disabled={!subcategoriaId}
            >
              Continuar
            </button>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Nenhuma subcategoria cadastrada nesta categoria.</p>
        )}
      </div>
    </ModalShell>
  );
}

// Ticket médio travado do Backoffice, um por subcategoria de Viagens (2.5.01–04) — só leitura
// aqui (necessário pra estimativa ao vivo do lançamento). Edição migrou para a tela dedicada
// Premissas · Viagens (ver PremissasViagemView.js) — decisão do usuário de não deixar o mesmo
// dado editável em dois lugares diferentes.
function CartaoTicketViagemDespesaSomenteLeitura({ C, ticket }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <label style={{ fontSize: 10, color: C.muted }}>{ticket.categoria_nome}</label>
      <div style={{ ...inputStyle(C), padding: "6px 8px", opacity: 0.7, background: C.bg }}>
        {ticket.ticket_medio}
      </div>
    </div>
  );
}

function faixaViagemDespesaVazia() {
  return { mes_inicio: 1, mes_fim: 12, n_pessoas: 0, n_viagens: 0, n_dias: 1, reajuste_pct: 0, verba_relacionamento: 0 };
}

// 1 lançamento aqui = uma linha por subcategoria de Viagens (Alimentação, Hospedagem, Transporte
// sempre; Passagem Aérea só se o checkbox "Incluir Passagem Aérea" estiver marcado) dentro do
// departamento atual — usa o ticket médio ÚNICO/global do Backoffice (DespesaTicketViagem),
// compartilhado por TODOS os departamentos de Despesas Gerais (decisão do usuário: tratar toda a
// Despesas Gerais como 1 projeto Backoffice, em vez de um ticket por departamento). "Adicionar
// período" nunca cria linha nova, só mais uma faixa dentro dessas linhas.
function ModalGrupoViagemDespesa({ C, ano, departamento, grupoExistente, onClose, onCriar, onAtualizar }) {
  const editando = !!grupoExistente;

  const [itemNome, setItemNome] = useState(grupoExistente?.item_nome || "");
  const [faixas, setFaixas] = useState(() => (
    grupoExistente?.faixas?.length ? grupoExistente.faixas.map((f) => ({ ...f })) : [faixaViagemDespesaVazia()]
  ));
  const [incluirPassagemAerea, setIncluirPassagemAerea] = useState(grupoExistente?.incluirPassagemAerea ?? true);
  const [incluirVerbaRelacionamento, setIncluirVerbaRelacionamento] = useState(grupoExistente?.incluirVerbaRelacionamento ?? false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    getTicketsViagemDespesasGerais(ano).then(setTickets).catch(() => {});
  }, [ano]);

  function atualizarFaixa(idx, chave, valor) {
    setFaixas((fs) => fs.map((f, i) => {
      if (i !== idx) return f;
      const nova = { ...f, [chave]: valor };
      if (chave === "mes_inicio" && Number(valor) > Number(nova.mes_fim)) nova.mes_fim = valor;
      return nova;
    }));
  }

  function adicionarFaixa() {
    setFaixas((fs) => {
      const ultimoFim = fs.length ? Number(fs[fs.length - 1].mes_fim) : 0;
      const proximoInicio = Math.min(ultimoFim + 1, 12);
      return [...fs, { ...faixaViagemDespesaVazia(), mes_inicio: proximoInicio, mes_fim: 12 }];
    });
  }

  function removerFaixa(idx) {
    setFaixas((fs) => (fs.length > 1 ? fs.filter((_, i) => i !== idx) : fs));
  }

  function estimativaFaixa(faixa) {
    const fatorBase = Number(faixa.n_viagens || 0) * Number(faixa.n_pessoas || 0) * (1 + Number(faixa.reajuste_pct || 0));
    const dias = Number(faixa.n_dias || 1);
    const totalTicket = tickets.reduce((acc, t) => {
      if (t.categoria_codigo === CODIGO_CATEGORIA_PASSAGEM_AEREA && !incluirPassagemAerea) return acc;
      const fator = CODIGOS_SUBCATEGORIAS_VIAGEM_DIAS.includes(t.categoria_codigo) ? fatorBase * dias : fatorBase;
      return acc + t.ticket_medio * fator;
    }, 0);
    return totalTicket + (incluirVerbaRelacionamento ? Number(faixa.verba_relacionamento || 0) : 0);
  }

  async function salvar() {
    if (!itemNome.trim()) {
      setErro("Informe o nome do lançamento.");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      const dados = {
        item_nome: itemNome.trim(),
        incluir_passagem_aerea: incluirPassagemAerea,
        incluir_verba_relacionamento: incluirVerbaRelacionamento,
        faixas: faixas.map((f) => ({
          mes_inicio: Number(f.mes_inicio), mes_fim: Number(f.mes_fim),
          n_pessoas: Number(f.n_pessoas) || 0, n_viagens: Number(f.n_viagens) || 0,
          n_dias: Number(f.n_dias) || 1, reajuste_pct: Number(f.reajuste_pct) || 0,
          verba_relacionamento: Number(f.verba_relacionamento) || 0,
        })),
      };
      if (editando) await onAtualizar(grupoExistente.item_grupo_id, dados);
      else await onCriar({ departamento, ano, ...dados });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <ModalShell C={C} title={editando ? "Editar Viagem" : "Lançar Viagem"} onClose={onClose} maxWidth={500}>
      {erro && (
        <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
          {erro}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
          Lança de uma vez o custo nas subcategorias de Viagens (Alimentação, Hospedagem, Passagem Aérea e Transporte) de <strong style={{ color: C.text }}>{departamento}</strong>, usando o ticket médio do Backoffice abaixo — compartilhado por todos os departamentos de Despesas Gerais. Verba de Relacionamento é opcional e não usa ticket médio.
        </p>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer" }}>
          <input type="checkbox" checked={incluirPassagemAerea} onChange={(e) => setIncluirPassagemAerea(e.target.checked)} />
          Incluir Passagem Aérea neste lançamento
        </label>
        {!incluirPassagemAerea && (
          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Desmarcado: esta viagem não terá custo de Passagem Aérea (ex.: deslocamento terrestre).
          </p>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer" }}>
          <input type="checkbox" checked={incluirVerbaRelacionamento} onChange={(e) => setIncluirVerbaRelacionamento(e.target.checked)} />
          Incluir Verba de Relacionamento neste lançamento (opcional)
        </label>
        {incluirVerbaRelacionamento && (
          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Valor manual, não usa ticket médio — informe o valor por período abaixo.
          </p>
        )}

        {tickets.length > 0 && (
          <div style={{ background: C.subcategoriaBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Ticket Médio de Viagem (Backoffice)
              </span>
              <span style={{ fontSize: 10, color: C.muted }}>Somente leitura — edite em Premissas · Viagens</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
              {tickets.map((t) => (
                <CartaoTicketViagemDespesaSomenteLeitura key={t.categoria_codigo} C={C} ticket={t} />
              ))}
            </div>
          </div>
        )}

        <Campo label="Nome do lançamento" C={C}>
          <input style={inputStyle(C)} value={itemNome} onChange={(e) => setItemNome(e.target.value)} placeholder="Ex: Missão em campo — Setembro" />
        </Campo>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {faixas.map((faixa, idx) => (
            <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Período {idx + 1}</span>
                {faixas.length > 1 && (
                  <IconBtn title="Remover este período" onClick={() => removerFaixa(idx)} C={C} danger><Trash2 size={13} /></IconBtn>
                )}
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <Campo label="Nº pessoas" C={C}>
                  <input style={inputStyle(C)} type="number" min={0} value={faixa.n_pessoas} onChange={(e) => atualizarFaixa(idx, "n_pessoas", e.target.value)} />
                </Campo>
                <Campo label="Nº viagens" C={C}>
                  <input style={inputStyle(C)} type="number" min={0} value={faixa.n_viagens} onChange={(e) => atualizarFaixa(idx, "n_viagens", e.target.value)} />
                </Campo>
                <Campo label="Nº dias" C={C}>
                  <input style={inputStyle(C)} type="number" min={1} value={faixa.n_dias ?? 1} onChange={(e) => atualizarFaixa(idx, "n_dias", e.target.value)} />
                </Campo>
              </div>
              <p style={{ margin: 0, fontSize: 10, color: C.muted }}>
                Nº dias multiplica o custo de Alimentação e Hospedagem (não afeta Passagem Aérea/Transporte).
              </p>

              <Campo label="Reajuste (fração, opcional)" C={C}>
                <input style={inputStyle(C)} type="number" step="0.01" value={faixa.reajuste_pct} onChange={(e) => atualizarFaixa(idx, "reajuste_pct", e.target.value)} />
              </Campo>

              {incluirVerbaRelacionamento && (
                <Campo label="Verba de Relacionamento (R$/mês, manual)" C={C}>
                  <input style={inputStyle(C)} type="number" min={0} step="0.01" value={faixa.verba_relacionamento ?? 0} onChange={(e) => atualizarFaixa(idx, "verba_relacionamento", e.target.value)} />
                </Campo>
              )}

              <div style={{ display: "flex", gap: 12 }}>
                <Campo label="Mês início" C={C}>
                  <select style={inputStyle(C)} value={faixa.mes_inicio} onChange={(e) => atualizarFaixa(idx, "mes_inicio", Number(e.target.value))}>
                    {MESES_ABREV.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </Campo>
                <Campo label="Mês fim" C={C}>
                  <select style={inputStyle(C)} value={faixa.mes_fim} onChange={(e) => atualizarFaixa(idx, "mes_fim", Number(e.target.value))}>
                    {MESES_ABREV.map((m, i) => i + 1 >= faixa.mes_inicio && <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </Campo>
              </div>

              {(tickets.length > 0 || incluirVerbaRelacionamento) && (
                <div style={{ background: C.subcategoriaBg, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
                  Custo estimado por mês neste período: <strong>{formatCurrency(estimativaFaixa(faixa))}</strong>
                </div>
              )}
            </div>
          ))}
        </div>

        <button type="button" onClick={adicionarFaixa} style={{
          display: "flex", alignItems: "center", gap: 4, background: "transparent",
          border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 12px",
          color: C.brand, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-start",
        }}>
          <Plus size={13} /> Adicionar período
        </button>

        <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
          Cada período vale, sem dividir, em todo mês de "Mês início" a "Mês fim" — use mais de um período quando o nº de pessoas/viagens/dias mudar ao longo do ano.
        </p>
      </div>

      <ModalFooter
        C={C} salvando={salvando}
        onCancel={onClose}
        onSave={salvar}
        labelSalvar={editando ? "Salvar alterações" : "Lançar viagem"}
      />
    </ModalShell>
  );
}

// "Lançar Kit Admissão" — 1 lançamento cria 5 linhas fixas (Fone/Teclado/Mouse + Notebook/Monitor
// do tipo escolhido) em 2.7.01 de <departamento>, usando o ticket já cadastrado em Premissas ·
// Equipamentos e Materiais (sem ticket próprio, ao contrário de Viagem). Vale para 1 mês só
// (compra pontual), com quantidade sugerida a partir de Mão de Obra (contratações do mês − kits
// já lançados).
function ModalGrupoKitAdmissaoDespesa({ C, ano, departamento, grupoExistente, onClose, onCriar, onAtualizar }) {
  const editando = !!grupoExistente;

  const [tipoKit, setTipoKit] = useState(grupoExistente?.tipo_kit || "basico");
  const [mes, setMes] = useState(grupoExistente?.mes || 1);
  const [quantidade, setQuantidade] = useState(grupoExistente?.quantidade ?? 1);
  const [itemNome, setItemNome] = useState(grupoExistente?.item_nome || "");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [tickets, setTickets] = useState([]);
  const [sugestao, setSugestao] = useState(null);

  useEffect(() => {
    getEquipamentosTicketMedio(ano).then(setTickets).catch(() => {});
  }, [ano]);

  // Sugestão só faz sentido ao criar (editando já vem com a quantidade real lançada) — recalcula
  // sempre que o mês muda, contratações do mês neste departamento menos kits já lançados nele.
  useEffect(() => {
    if (editando) return;
    getReconciliacaoKitsAdmissao(ano)
      .then((linhas) => {
        const linhaDepartamento = linhas.find((l) => l.departamento === departamento);
        const linhaMes = linhaDepartamento?.meses.find((m) => m.mes === mes);
        const sugerido = Math.max(0, (linhaMes?.contratacoes || 0) - (linhaMes?.kits_lancados || 0));
        setSugestao(linhaMes ? { ...linhaMes, sugerido } : null);
        setQuantidade(sugerido);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, ano, departamento, editando]);

  const ticketPorCodigo = useMemo(() => Object.fromEntries(tickets.map((t) => [t.codigo, t.ticket_medio])), [tickets]);
  const codigosKit = [...ITENS_KIT_ADMISSAO_COMUNS, ...ITENS_KIT_ADMISSAO_VARIAVEIS[tipoKit]];
  const estimativa = codigosKit.reduce((acc, codigo) => acc + (ticketPorCodigo[codigo] || 0), 0) * Number(quantidade || 0);

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      const dados = { tipo_kit: tipoKit, mes: Number(mes), quantidade: Number(quantidade) || 0, item_nome: itemNome.trim() || null };
      if (editando) await onAtualizar(grupoExistente.item_grupo_id, dados);
      else await onCriar({ departamento, ano, ...dados });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <ModalShell C={C} title={editando ? "Editar Kit Admissão" : "Lançar Kit Admissão"} onClose={onClose} maxWidth={460}>
      {erro && (
        <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
          {erro}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
          1 Fone + 1 Teclado + 1 Mouse + 1 Notebook + 1 Monitor para <strong style={{ color: C.text }}>{departamento}</strong>, usando o ticket cadastrado em Premissas · Equipamentos e Materiais — vale para 1 mês específico, não um período.
        </p>

        <Campo label="Tipo de kit" C={C}>
          <div style={{ display: "flex", gap: 8 }}>
            {Object.entries(KIT_ADMISSAO_LABELS).map(([valor, label]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setTipoKit(valor)}
                style={{
                  flex: 1, padding: "9px 0", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                  border: `1px solid ${tipoKit === valor ? C.brand : C.border}`,
                  background: tipoKit === valor ? C.brand : "transparent",
                  color: tipoKit === valor ? "#fff" : C.text,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </Campo>

        <div style={{ display: "flex", gap: 12 }}>
          <Campo label="Mês" C={C}>
            <select style={inputStyle(C)} value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {MESES_ABREV.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </Campo>
          <Campo label="Quantidade de kits" C={C}>
            <input style={inputStyle(C)} type="number" min={0} value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
          </Campo>
        </div>

        {sugestao && (
          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Contratações de {departamento} em {MESES_ABREV[mes - 1]}: <strong>{sugestao.contratacoes}</strong> · Kits já lançados: <strong>{sugestao.kits_lancados}</strong> · Sugestão: <strong>{sugestao.sugerido}</strong>
          </p>
        )}

        <Campo label="Nome/observação (opcional)" C={C}>
          <input style={inputStyle(C)} value={itemNome} onChange={(e) => setItemNome(e.target.value)} placeholder="Ex: Admissões de Setembro" />
        </Campo>

        {tickets.length > 0 && (
          <div style={{ background: C.subcategoriaBg, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
            Custo estimado neste mês: <strong>{formatCurrency(estimativa)}</strong>
          </div>
        )}
      </div>

      <ModalFooter
        C={C} salvando={salvando}
        onCancel={onClose}
        onSave={salvar}
        labelSalvar={editando ? "Salvar alterações" : "Lançar kit"}
      />
    </ModalShell>
  );
}

// Modal simples de item, padrão Custos Gerais por Projeto (ModalItem em CustosGeraisProjetoView.js)
// — Nome + Valor + Período(s) repetíveis, sem reajustes/expansões. "Tipo de lançamento" só
// aparece para categoria especial (2.6.11 Assinaturas e Sistemas, ver categoriaEhEspecialDespesa),
// liberando "Quantidade de usuários" (valor unitário × nº usuários) em vez do valor manual —
// Despesas Gerais não tem "alocacao_projeto" (não existe conceito de projeto aqui).
function ModalNovoItemSimples({ C, ano, departamento, categoria, itemExistente, onClose, onCriar, onAtualizar }) {
  const editando = !!itemExistente;
  const tiposPermitidos = tiposPermitidosParaCategoriaDespesa(categoria);
  const especial = categoriaEhEspecialDespesa(categoria);

  const [itemNome, setItemNome] = useState(itemExistente?.nome || "");
  const [tipoLancamento, setTipoLancamento] = useState(itemExistente?.tipo_lancamento || "valor_manual");
  const [faixas, setFaixas] = useState(() => (
    itemExistente?.faixas_padrao?.length ? itemExistente.faixas_padrao.map((f) => ({ ...f })) : [faixaPadraoVazia()]
  ));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  function atualizarFaixa(idx, chave, valor) {
    setFaixas((fs) => fs.map((f, i) => {
      if (i !== idx) return f;
      const nova = { ...f, [chave]: valor };
      if (chave === "mes_inicio" && Number(valor) > Number(nova.mes_fim)) nova.mes_fim = valor;
      return nova;
    }));
  }

  function adicionarFaixa() {
    setFaixas((fs) => {
      const ultimoFim = fs.length ? Number(fs[fs.length - 1].mes_fim) : 0;
      const proximoInicio = Math.min(ultimoFim + 1, 12);
      return [...fs, { ...faixaPadraoVazia(), mes_inicio: proximoInicio, mes_fim: 12 }];
    });
  }

  function removerFaixa(idx) {
    setFaixas((fs) => (fs.length > 1 ? fs.filter((_, i) => i !== idx) : fs));
  }

  async function salvar() {
    if (!categoria) return;
    if (!itemNome.trim()) {
      setErro("Informe o nome do item.");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      const dados = {
        categoria_id: categoria.id, nome: itemNome.trim(), ano, departamento,
        modelo_valor: "faixas", tipo_lancamento_padrao: tipoLancamento,
        faixas_padrao: faixas.map((f) => ({
          mes_inicio: Number(f.mes_inicio), mes_fim: Number(f.mes_fim),
          valor: Number(f.valor) || 0, valor_unitario: Number(f.valor_unitario) || 0, usuarios: Number(f.usuarios) || 0,
        })),
      };
      if (editando) await onAtualizar(itemExistente.id, dados);
      else await onCriar(dados);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{editando ? "Editar item" : "Novo item"}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}><X size={20} /></button>
        </div>

        {erro && (
          <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: C.subcategoriaBg, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
            Categoria: <strong>{categoria ? `${categoria.codigo} ${categoria.nome}` : "—"}</strong>
          </div>
          <Campo label="Nome do item" C={C}>
            <input value={itemNome} onChange={(e) => setItemNome(e.target.value)} placeholder={categoria?.nome} style={inputStyle(C)} />
          </Campo>

          {especial && (
            <Campo label="Tipo de lançamento" C={C}>
              <select value={tipoLancamento} onChange={(e) => setTipoLancamento(e.target.value)} style={inputStyle(C)}>
                {TIPOS_LANCAMENTO_DESPESA_OPTIONS.filter((o) => tiposPermitidos.includes(o.value)).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span style={{ fontSize: 11, color: C.muted }}>
                {TIPOS_LANCAMENTO_DESPESA_OPTIONS.find((o) => o.value === tipoLancamento)?.descricao}
              </span>
            </Campo>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {faixas.map((faixa, idx) => (
              <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Período {idx + 1}</span>
                  {faixas.length > 1 && (
                    <button onClick={() => removerFaixa(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, display: "inline-flex" }} title="Remover este período">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>

                {tipoLancamento === "valor_manual" && (
                  <Campo label="Valor (R$)" C={C}>
                    <input type="number" value={faixa.valor} onChange={(e) => atualizarFaixa(idx, "valor", e.target.value)} style={inputStyle(C)} />
                  </Campo>
                )}

                {tipoLancamento === "quantidade_usuarios" && (
                  <div style={{ display: "flex", gap: 12 }}>
                    <Campo label="Valor unitário (R$)" C={C}>
                      <input type="number" value={faixa.valor_unitario} onChange={(e) => atualizarFaixa(idx, "valor_unitario", e.target.value)} style={inputStyle(C)} />
                    </Campo>
                    <Campo label="Nº usuários" C={C}>
                      <input type="number" value={faixa.usuarios} onChange={(e) => atualizarFaixa(idx, "usuarios", e.target.value)} style={inputStyle(C)} />
                    </Campo>
                  </div>
                )}

                <div style={{ display: "flex", gap: 12 }}>
                  <Campo label="Mês início" C={C}>
                    <select value={faixa.mes_inicio} onChange={(e) => atualizarFaixa(idx, "mes_inicio", Number(e.target.value))} style={inputStyle(C)}>
                      {MESES_ABREV.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Mês fim" C={C}>
                    <select value={faixa.mes_fim} onChange={(e) => atualizarFaixa(idx, "mes_fim", Number(e.target.value))} style={inputStyle(C)}>
                      {MESES_ABREV.map((m, i) => i + 1 >= faixa.mes_inicio && <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </Campo>
                </div>
              </div>
            ))}
          </div>

          <button onClick={adicionarFaixa} style={{ ...btnStyle(C.brand, true), alignSelf: "flex-start", padding: "6px 12px", fontSize: 12 }}>
            <Plus size={13} /> Adicionar período
          </button>

          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Cada período vale, sem dividir, em todo mês de "Mês início" a "Mês fim" — use mais de um período quando o valor mudar ao longo do ano (ex.: Jan–Mar com um valor, Jun–Out com outro).
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }} onClick={onClose}>Cancelar</button>
          <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }} onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : editando ? "Salvar alterações" : "Criar item"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, children, C }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none", width: "100%",
  };
}

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function thStyle(C) {
  return {
    padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    position: "sticky", top: 0, zIndex: 3, background: C.card,
  };
}

// `sticky` trava a coluna na rolagem horizontal (Código/Item): { left, background }.
// O background precisa ser opaco e explícito (igual ao da linha) pois a célula fixa
// fica por cima do conteúdo das outras colunas ao rolar.
function tdStyle(C, strong = false, sticky = null) {
  const base = {
    padding: "8px 10px", fontSize: 12, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
  if (!sticky) return base;
  return { ...base, position: "sticky", left: sticky.left, zIndex: 2, background: sticky.background };
}

function IconBtn({ children, onClick, title, C, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: 6, border: `1px solid ${C.border}`,
        background: "transparent", color: danger ? C.danger : C.text, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function ModalShell({ title, children, onClose, C, maxWidth = 480 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ C, salvando, onCancel, onSave, labelSalvar }) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
      <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
        onClick={onCancel}>
        Cancelar
      </button>
      <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }}
        onClick={onSave} disabled={salvando}>
        {salvando ? "Salvando..." : labelSalvar}
      </button>
    </div>
  );
}
